import axios from 'axios';
import {
  extract_error_message,
  parse_response_data,
} from '@/utils/api_response_helpers';
import {
  CREATE_TEMPLATE_FORM_URL,
  UPDATE_TEMPLATE_FORM_URL,
  GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
  get_request_config,
} from '@/utils/system_constants/constant';
import { normalize_template_form } from './template_form_management_helpers';

const template_form_management_actions_methods = {
  set_active_tab(tab) {
    this.active_tab = tab === 'workflows' ? 'workflows' : 'forms';

    if (this.$router?.replace) {
      this.$router.replace({
        query: {
          ...(this.$route?.query || {}),
          tab: this.active_tab,
        },
      }).catch(() => {});
    }

    if (this.active_tab === 'forms') {
      this.load_forms_tab_data();
    }
  },

  reset_form_modal() {
    this.form_name = '';
    this.form_description = '';
  },

  handle_banner_action() {
    if (this.is_forms_tab) {
      this.reset_form_modal();
      return;
    }

    this.$refs.esigningWorkflows?.open_create_workflow_modal?.();
  },

  async save_template_form() {
    const form_name = this.form_name.trim();
    const form_description = this.form_description.trim();

    if (!form_name) {
      this.show_warning('Template Form', 'Please enter a template form name.');
      return;
    }

    if (this.is_saving) return;

    this.is_saving = true;
    const loader = await this.show_loader('Creating template form...');

    try {
      const response = await axios.post(
        CREATE_TEMPLATE_FORM_URL,
        {
          form_name,
          form_description,
          is_active: true,
        },
        get_request_config(),
      );

      const parsed = parse_response_data(response);

      if (parsed?.status !== 'success') {
        this.show_error(parsed?.message || 'Failed to create template form.');
        return;
      }

      const new_form = normalize_template_form(parsed?.data);

      if (new_form) {
        this.template_forms = [new_form, ...this.template_forms];
      } else {
        await this.get_template_forms();
      }

      this.close_modal('create_template_form');
      this.reset_form_modal();
      this.show_success(parsed?.message || 'Template form created successfully.');
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to create template form.'),
      );
    } finally {
      await this.close_loader(loader);
      this.is_saving = false;
    }
  },

  async open_mapping(form) {
    if (!form?.template_form_id) {
      this.show_warning('Manage Form', 'Please select a template form first.');
      return;
    }

    try {
      const response = await axios.get(
        GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
        get_request_config({
          params: { template_form_id: form.template_form_id },
        }),
      );
      const parsed = parse_response_data(response);
      const assignment_items = Array.isArray(parsed?.data) ? parsed.data : [];
      const active_assignment =
        assignment_items.find((item) => item?.is_active !== false) || null;

      if (!active_assignment?.link_id || !active_assignment?.onboarding_form_id) {
        this.show_warning('Manage Form', 'Please assign an onboarding form before mapping questions.');
        return;
      }

      this.$router.push({
        name: 'TemplateQuestionMapping',
        query: {
          template_form_id: String(form.template_form_id),
          template_form_name: String(form.form_name || ''),
          template_form_description: String(form.form_description || ''),
          template_builder_form_id: String(form.survey_form_id || ''),
          onboarding_form_id: String(active_assignment.onboarding_form_id || ''),
          onboarding_form_name: String(active_assignment.onboarding_form_name || ''),
          form_link_id: String(active_assignment.link_id || ''),
        },
      });
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to load assigned onboarding form for question mapping.'),
      );
    }
  },

  format_date(value) {
    const date = value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return 'Unknown date';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  },
  open_edit_modal(form) {
 
  this.selected_form = form;
  this.edit_form_name = form.form_name;
  this.edit_form_description = form.form_description;
  this.edit_form_is_active = form.is_active;
  this.edit_form_id = form.template_form_id;
},
async update_template_form() {
 
  const form_name = this.edit_form_name.trim();
  const form_description = this.edit_form_description.trim();

  if (!form_name) {
    this.show_warning('Template Form', 'Please enter a template form name.');
    return;
  }

  if (this.is_updating) return;

  this.is_updating = true;
  const loader = await this.show_loader('Updating template form...');

  try {
    const response = await axios.post(
      UPDATE_TEMPLATE_FORM_URL,
      {
        template_form_id: this.edit_form_id,
        form_name,
        form_description,
        is_active: this.edit_form_is_active,
      },
      get_request_config(),
    );

    const parsed = parse_response_data(response);

    if (parsed?.status !== 'success') {
      this.show_error(parsed?.message || 'Failed to update template form.');
      return;
    }

    const updated_form = normalize_template_form(parsed?.data);

    if (updated_form) {
      this.template_forms = this.template_forms.map((form) =>
        form.template_form_id === this.edit_form_id ? updated_form : form,
      );
    } else {
      await this.get_template_forms();
    }

    this.close_modal('edit_template_form');
    this.show_success(parsed?.message || 'Template form updated successfully.');
  } catch (error) {
    this.show_error(
      extract_error_message(error, 'Failed to update template form.'),
    );
  } finally {
    await this.close_loader(loader);
    this.is_updating = false;
  }
},
};

export default template_form_management_actions_methods;
